import { useForm } from '@tanstack/react-form'
import { useDebounce } from 'commons/hooks/common'
import { CompanySelect, FF, SearchInput, Select, Toggle } from 'ui/components'
import { FilterPanel } from 'ui/components/Table'
import { useCrbMonitorListContext } from '~bank-crb-monitor/list/context'
import { CrbMonitorListPayload } from '~bank-crb-monitor/types'

import { s } from './style'

export const Filter = () => {
  const { routeApi, data } = useCrbMonitorListContext()
  const navigate = routeApi.useNavigate()
  const {
    licensesOptions,
    crbLicenseStatusOptions,
    connectionStatusOptions,
    relationshipOptions,
    customerStatusOptions
  } = data
  const searchParams = routeApi.useSearch()
  const isChanged = routeApi.useLoaderDeps({ select: (s) => s.isChanged })

  const { allRelationships, relationshipId, connectionStatus } = searchParams

  const initRelationshipId = allRelationships ? null : relationshipId ? relationshipId : 'my'

  const formApi = useForm({
    defaultValues: {
      ...searchParams,
      relationshipId: initRelationshipId
    } as Omit<CrbMonitorListPayload, 'page' | 'size' | 'sortDesc'>
  })

  const clear = () => {
    formApi.reset()
    navigate({ search: undefined })
  }

  const updateSearchParams = (newValue: string) =>
    navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, searchTerm: newValue }) })

  const throttledSearch = useDebounce(updateSearchParams, 300)

  return (
    <FilterPanel
      isDirty={isChanged}
      onReset={clear}
      expanded
      customActions={
        <formApi.Field
          name="searchTerm"
          listeners={{
            onChange: ({ value }) => {
              throttledSearch(value)
              return undefined
            }
          }}
          children={(field) => <FF field={field} input={(inputProps) => <SearchInput {...inputProps} />} />}
        />
      }
    >
      <s.Columns1>
        <s.Column>
          <formApi.Field
            name="relationshipId"
            listeners={{
              onChange: ({ value }) => {
                if (value) {
                  if (value === 'my') {
                    navigate({
                      to: '.',
                      search: (prev) => ({
                        ...prev,
                        page: 1,
                        allRelationships: false,
                        relationshipId: null,
                        assignedRelationshipsOnly: true
                      })
                    })
                  } else {
                    navigate({
                      to: '.',
                      search: (prev) => ({
                        ...prev,
                        page: 1,
                        allRelationships: false,
                        relationshipId: value,
                        assignedRelationshipsOnly: false
                      })
                    })
                  }
                } else {
                  navigate({
                    to: '.',
                    search: (prev) => ({
                      ...prev,
                      page: 1,
                      allRelationships: true,
                      relationshipId: null,
                      assignedRelationshipsOnly: false
                    })
                  })
                }

                return undefined
              }
            }}
            children={(field) => (
              <FF
                field={field}
                label="Relationship"
                input={(p) => <CompanySelect {...p} preloadedOptions={relationshipOptions} hideMyCompaniesFilter />}
              />
            )}
          />
        </s.Column>
        <s.Column>
          <formApi.Field
            name="licenseId"
            listeners={{
              onChange: ({ value }) => {
                navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, licenseId: value }) })
                return undefined
              }
            }}
            children={(field) => (
              <FF
                field={field}
                label="Orbit License"
                input={(p) => <Select {...p} data={licensesOptions} searchable />}
              />
            )}
          />
        </s.Column>
        <s.Column>
          <formApi.Field
            name="connectionStatus"
            listeners={{
              onChange: ({ value }) => {
                navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, connectionStatus: value }) })
                return undefined
              }
            }}
            children={(field) => (
              <FF
                field={field}
                label="Connection Status"
                input={(p) => <Select {...p} data={connectionStatusOptions} />}
              />
            )}
          />
        </s.Column>
        <s.Column>
          <formApi.Field
            name="crbLicenseStatus"
            validators={{
              onChange: ({ value }) => {
                navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, crbLicenseStatus: value }) })
                return undefined
              }
            }}
            children={(field) => (
              <FF
                field={field}
                label="CRB License Status"
                input={(p) => <Select {...p} data={crbLicenseStatusOptions} />}
              />
            )}
          />
        </s.Column>
        <s.Column>
          <formApi.Field
            name="customerStatus"
            listeners={{
              onChange: ({ value }) => {
                navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, customerStatus: value }) })
                return undefined
              }
            }}
            children={(field) => (
              <FF field={field} label="Customer Status" input={(p) => <Select {...p} data={customerStatusOptions} />} />
            )}
          />
        </s.Column>
      </s.Columns1>
      <s.Columns2>
        <s.Column>
          <Toggle
            value={connectionStatus === ''}
            onChange={(value) => {
              navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, connectionStatus: value ? '' : null }) })
              formApi.setFieldValue('connectionStatus', value ? '' : null)
            }}
            label="Not connected licenses"
          />
        </s.Column>
        <s.Column>
          <formApi.Field
            name="onlyAlerted"
            listeners={{
              onChange: ({ value }) => {
                navigate({ to: '.', search: (prev) => ({ ...prev, page: 1, onlyAlerted: value }) })
                return undefined
              }
            }}
          >
            {(field) => <Toggle value={field.state.value} onChange={field.handleChange} label="Alerted licenses" />}
          </formApi.Field>
        </s.Column>
      </s.Columns2>
    </FilterPanel>
  )
}
